/**
 * Retiros parciales de efectivo (spec 11, enmienda 2026-10-09). El cajero saca efectivo de su caja abierta y dice a
 * quién se lo entrega; la dueña (o quien tenga el permiso) confirma desde su usuario que lo recibió, o lo deja
 * observado. La caja sigue abierta y el arqueo deja de esperar ese efectivo. Comprobante imprimible por retiro.
 */
import { Money, type Pagina, type RetiroDTO, type SesionCajaDTO } from '@mg/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Banknote, Check, Printer, TriangleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { toast } from 'sonner';
import { AyudaVentana } from '@/ayuda/BotonAyuda.tsx';
import { FiltroFechas } from '@/componentes/FiltroFechas.tsx';
import { PieDePaginas } from '@/componentes/PieDePaginas.tsx';
import { mostrarRegistrado } from '@/componentes/Registrado.tsx';
import { Badge } from '@/componentes/ui/badge.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/componentes/ui/card.tsx';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/componentes/ui/dialog.tsx';
import { Input, Select } from '@/componentes/ui/input.tsx';
import { Campo } from '@/componentes/ui/label.tsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/componentes/ui/table.tsx';
import { api, ErrorApi, get, mensajeDeError, post } from '@/lib/api.ts';
import { useAuth } from '@/lib/auth.tsx';
import { useRegistroImpresion } from '@/lib/impresion.ts';
import { normalizarDecimal } from '@/lib/montos.ts';
import { formatearFechaHora } from '@/lib/utils.ts';

const simbolo = (moneda: string) => (moneda === 'BS' ? 'Bs' : '$');
const monto = (r: Pick<RetiroDTO, 'moneda' | 'monto'>) => `${simbolo(r.moneda)} ${Money.parse(r.monto).toDisplay()}`;
const ESTADO: Record<RetiroDTO['estado'], { texto: string; v: 'ok' | 'pendiente' | 'critico' }> = {
  por_confirmar: { texto: 'Por confirmar', v: 'pendiente' },
  confirmado: { texto: 'Confirmado', v: 'ok' },
  observado: { texto: 'Observado', v: 'critico' },
};
export const clavesRetiros = { lista: (f: Record<string, unknown> = {}) => ['caja', 'retiros', f] as const };

/** Aviso del límite de efectivo. No lleva montos: solo que se pasó, cuántos avisos van y si la caja quedó bloqueada. */
export function AvisoRetiro({ retiro, alRetirar }: { retiro: SesionCajaDTO['retiro']; alRetirar?: () => void }) {
  if (!retiro?.excede) return null;
  return (
    <div className={`flex flex-wrap items-center gap-3 rounded-lg border p-3 text-sm ${retiro.bloqueada ? 'border-red-300 bg-red-50 text-red-900' : 'border-amber-300 bg-amber-50 text-amber-900'}`} role="alert" data-testid="aviso-retiro" data-bloqueada={retiro.bloqueada ? 'si' : 'no'}>
      <Banknote className="size-4" />
      <span className="flex-1">
        {retiro.bloqueada
          ? 'La caja superó el límite de efectivo y ya se dieron todos los avisos: no se puede cobrar hasta registrar el retiro parcial.'
          : `La caja superó el límite de efectivo: haz un retiro parcial. Aviso ${retiro.avisos} de ${retiro.max_avisos}; después la caja deja de cobrar.`}
      </span>
      {alRetirar ? <Button size="sm" variant={retiro.bloqueada ? 'destructive' : 'outline'} onClick={alRetirar} data-testid="aviso-retiro-registrar">Registrar retiro</Button> : null}
    </div>
  );
}

/** El cajero registra cuánto sacó y a quién se lo entregó. */
export function DialogoRetiro({ abierto, cerrar, cajaNombre, sesionId }: { abierto: boolean; cerrar: () => void; cajaNombre: string; sesionId?: string }) {
  const qc = useQueryClient();
  const [moneda, setMoneda] = useState<'USD' | 'BS'>('USD');
  const [cuanto, setCuanto] = useState('');
  const [recibe, setRecibe] = useState('');
  const [nota, setNota] = useState('');
  // Clave de la operación: el doble clic o el reintento de red registran UN solo retiro (no se puede corregir después)
  const [clave, setClave] = useState(() => crypto.randomUUID());
  const registrar = useMutation({
    mutationFn: () => api<RetiroDTO>('POST', '/caja/retiros', { body: { moneda, monto: normalizarDecimal(cuanto), recibe: recibe.trim(), ...(nota.trim() ? { nota: nota.trim() } : {}), ...(sesionId ? { sesion_id: sesionId } : {}) }, headers: { 'idempotency-key': clave } }),
    onSuccess: async (r) => {
      mostrarRegistrado({
        titulo: `Retiro ${r.numero_formateado} registrado`,
        descripcion: `${monto(r)} de ${r.caja_nombre} · lo recibe ${r.recibe}`,
        detalle: [{ texto: r.estado === 'confirmado' ? 'Quedó confirmado.' : 'Queda por confirmar: quien lo recibe lo confirma desde su usuario.' }],
        imprimir: [{ etiqueta: 'Imprimir comprobante', ruta: `/imprimir/RETC/${r.id}` }],
      });
      setCuanto(''); setRecibe(''); setNota(''); setClave(crypto.randomUUID());
      await qc.invalidateQueries({ queryKey: ['caja'] });
      cerrar();
    },
    // Rechazo del servidor (4xx): clave nueva. Error de red o 5xx: se conserva (si sí se registró, reintentar devuelve el mismo)
    onError: (e) => { toast.error('No se pudo registrar el retiro', { description: mensajeDeError(e) }); if (e instanceof ErrorApi && e.status < 500) setClave(crypto.randomUUID()); },
  });
  const valido = /^\d+([.,]\d+)?$/.test(cuanto.trim()) && Number(normalizarDecimal(cuanto)) > 0 && recibe.trim().length >= 3;
  return (
    <Dialog open={abierto} onOpenChange={(o) => { if (!o) cerrar(); }}>
      <DialogContent data-testid="dialogo-retiro">
        <DialogHeader>
          <div className="flex items-center gap-1"><DialogTitle>Retiro parcial de efectivo · {cajaNombre}</DialogTitle><AyudaVentana tema="caja.retiro" /></div>
          <DialogDescription>Saca el efectivo, entrégalo y regístralo aquí. La caja sigue abierta; al cerrar ya no se cuenta este dinero. No es un gasto.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Campo etiqueta="Moneda" htmlFor="retiro-moneda">
            <Select id="retiro-moneda" value={moneda} onChange={(e) => setMoneda(e.target.value as 'USD' | 'BS')} data-testid="retiro-moneda">
              <option value="USD">Efectivo $</option>
              <option value="BS">Efectivo Bs</option>
            </Select>
          </Campo>
          <Campo etiqueta={`Monto retirado (${simbolo(moneda)})`} htmlFor="retiro-monto">
            <Input id="retiro-monto" inputMode="decimal" placeholder="0,00" value={cuanto} onChange={(e) => setCuanto(e.target.value)} className="text-right" data-testid="retiro-monto" />
          </Campo>
        </div>
        <Campo etiqueta="Se entrega a" htmlFor="retiro-recibe" ayuda="Nombre de quien lo recibe o la caja a la que pasa">
          <Input id="retiro-recibe" value={recibe} onChange={(e) => setRecibe(e.target.value)} maxLength={120} data-testid="retiro-recibe" />
        </Campo>
        <Campo etiqueta="Nota (opcional)" htmlFor="retiro-nota">
          <Input id="retiro-nota" value={nota} onChange={(e) => setNota(e.target.value)} maxLength={300} />
        </Campo>
        <DialogFooter>
          <Button variant="outline" onClick={cerrar}>Cancelar</Button>
          <Button disabled={!valido} cargando={registrar.isPending} onClick={() => registrar.mutate()} data-testid="retiro-registrar"><Banknote /> Registrar retiro</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Lista de retiros: el cajero ve los suyos; quien confirma o autoriza cierres, los de la sucursal (la dueña, todos). */
export function RetirosCard({ soloPorConfirmar = false, sesionId }: { soloPorConfirmar?: boolean; sesionId?: string }) {
  const { usuario, puede } = useAuth();
  const qc = useQueryClient();
  // Por página (25) y, en la lista general, «Desde / Hasta» (hallazgo T19: antes traía los 200 más recientes sin decirlo)
  const general = !soloPorConfirmar && !sesionId;
  const [rango, setRango] = useState({ desde: '', hasta: '' });
  const [pagina, setPagina] = useState(1);
  const filtros = { ...(soloPorConfirmar ? { estado: 'por_confirmar' } : {}), ...(sesionId ? { sesion_id: sesionId } : {}), ...(general && rango.desde ? { desde: rango.desde } : {}), ...(general && rango.hasta ? { hasta: rango.hasta } : {}), pagina, limite: 25 };
  const lista = useQuery({ queryKey: clavesRetiros.lista(filtros), queryFn: () => get<Pagina<RetiroDTO>>('/caja/retiros', filtros), refetchInterval: 60_000 });
  const [observando, setObservando] = useState<RetiroDTO | null>(null);
  const [comentario, setComentario] = useState('');
  const confirmar = useMutation({
    mutationFn: ({ id, observado, c }: { id: string; observado: boolean; c?: string }) => post<RetiroDTO>(`/caja/retiros/${id}/confirmar`, { observado, ...(c ? { comentario: c } : {}) }),
    onSuccess: async (r) => {
      toast.success(r.estado === 'confirmado' ? `Retiro ${r.numero_formateado} confirmado` : `Retiro ${r.numero_formateado} observado`, { description: r.estado === 'observado' ? 'El cajero recibe el aviso.' : `${monto(r)} recibidos de ${r.caja_nombre}` });
      setObservando(null); setComentario('');
      await Promise.all([qc.invalidateQueries({ queryKey: ['caja'] }), qc.invalidateQueries({ queryKey: ['notificaciones'] })]);
    },
    onError: (e) => toast.error('No se pudo registrar', { description: mensajeDeError(e) }),
  });
  const confirma = puede('caja.confirmar-retiro');
  const filas = lista.data?.datos ?? [];
  if (soloPorConfirmar && filas.length === 0) return null;
  return (
    <Card data-testid={soloPorConfirmar ? 'retiros-por-confirmar' : 'retiros'}>
      <CardHeader>
        <div className="flex items-center gap-1"><CardTitle>{soloPorConfirmar ? 'Retiros de caja por confirmar' : 'Retiros parciales de efectivo'}</CardTitle>{soloPorConfirmar ? <AyudaVentana tema="caja.confirmar-retiros" /> : <AyudaVentana tema="caja.retiro" />}</div>
        <CardDescription>{soloPorConfirmar ? 'Efectivo que los cajeros dicen haber entregado. Confirma cuando lo recibas; si no cuadra, déjalo observado.' : 'Efectivo que salió de la caja durante el turno. No es un gasto: el cierre ya no lo cuenta.'}</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {general ? (
          <div className="flex justify-end px-4 pb-2">
            <FiltroFechas desde={rango.desde} hasta={rango.hasta} onChange={(desde, hasta) => { setRango({ desde, hasta }); setPagina(1); }} testid="retiros" />
          </div>
        ) : null}
        {filas.length === 0 ? <p className="p-4 text-sm text-muted-foreground">Sin retiros registrados.</p> : (
          <Table>
            <TableHeader><TableRow><TableHead>Retiro</TableHead><TableHead>Caja</TableHead><TableHead>Fecha</TableHead><TableHead className="text-right">Monto</TableHead><TableHead>Entregado a</TableHead><TableHead>Estado</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {filas.map((r) => (
                <TableRow key={r.id} data-testid={`retiro-${r.numero_formateado}`}>
                  <TableCell className="font-medium">{r.numero_formateado}</TableCell>
                  <TableCell>{r.caja_nombre}<span className="block text-[11px] text-muted-foreground">{r.cajero_nombre}{usuario?.sucursal_id ? '' : ` · ${r.sucursal_nombre}`}</span></TableCell>
                  <TableCell className="text-xs">{formatearFechaHora(r.registrado_en)}<span className="block text-muted-foreground">{r.registrado_por_nombre}</span></TableCell>
                  <TableCell className="text-right num">{monto(r)}{r.moneda === 'BS' ? <span className="block text-[11px] text-muted-foreground">≈ $ {Money.parse(r.monto_usd).toDisplay()}</span> : null}</TableCell>
                  <TableCell>{r.recibe}{r.nota ? <span className="block text-[11px] text-muted-foreground">{r.nota}</span> : null}</TableCell>
                  <TableCell><Badge variant={ESTADO[r.estado].v}>{ESTADO[r.estado].texto}</Badge>{r.confirmado_por_nombre ? <span className="block text-[11px] text-muted-foreground">{r.confirmado_por_nombre}</span> : null}{r.comentario ? <span className="block text-[11px] text-mg-rojo">{r.comentario}</span> : null}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {confirma && r.estado === 'por_confirmar' && r.registrado_por_nombre !== usuario?.nombre ? (
                        <>
                          <Button size="sm" cargando={confirmar.isPending} onClick={() => confirmar.mutate({ id: r.id, observado: false })} data-testid={`retiro-confirmar-${r.numero_formateado}`}><Check /> Lo recibí</Button>
                          <Button size="sm" variant="outline" onClick={() => setObservando(r)} data-testid={`retiro-observar-${r.numero_formateado}`}><TriangleAlert /> No cuadra</Button>
                        </>
                      ) : null}
                      <Button size="sm" variant="ghost" onClick={() => window.open(`/imprimir/RETC/${r.id}`, '_blank')} aria-label="Imprimir comprobante" data-testid={`retiro-imprimir-${r.numero_formateado}`}><Printer /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {lista.data && lista.data.total > lista.data.limite ? <div className="px-4 pb-3"><PieDePaginas total={lista.data.total} limite={lista.data.limite} pagina={pagina} setPagina={setPagina} nombre="retiros" testid="retiros-paginas" /></div> : null}
      </CardContent>
      <Dialog open={!!observando} onOpenChange={(o) => { if (!o) setObservando(null); }}>
        <DialogContent data-testid="dialogo-retiro-observar">
          <DialogHeader><div className="flex items-center gap-1"><DialogTitle>Retiro {observando?.numero_formateado}: no cuadra</DialogTitle><AyudaVentana tema="caja.confirmar-retiros" /></div><DialogDescription>Escribe qué recibiste o qué falta. El retiro queda observado y el cajero recibe el aviso.</DialogDescription></DialogHeader>
          <Campo etiqueta="Qué no cuadra" htmlFor="retiro-comentario"><Input id="retiro-comentario" value={comentario} onChange={(e) => setComentario(e.target.value)} maxLength={300} data-testid="retiro-comentario" /></Campo>
          <DialogFooter>
            <Button variant="outline" onClick={() => setObservando(null)}>Cancelar</Button>
            <Button variant="destructive" disabled={comentario.trim().length < 3} cargando={confirmar.isPending} onClick={() => observando && confirmar.mutate({ id: observando.id, observado: true, c: comentario.trim() })} data-testid="retiro-observar-confirmar">Dejar observado</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

const registrarCopiaRetiro = (id: string) => post<{ copia: boolean }>(`/caja/retiros/${id}/impresion`).then((r) => r.copia);

/** Comprobante del retiro (media carta vertical): dos firmas, quien entrega y quien recibe. */
export function ImprimirRetiro() {
  const { id } = useParams<{ id: string }>();
  const r = useQuery({ queryKey: ['caja', 'retiro', id], queryFn: () => get<RetiroDTO>(`/caja/retiros/${id}`), enabled: !!id });
  const empresa = useQuery({ queryKey: ['empresa'], queryFn: () => get<{ razon_social: string; rif: string; direccion_fiscal: string }>('/sucursales/empresa') });
  const copia = useRegistroImpresion(id, registrarCopiaRetiro);
  useEffect(() => {
    if (r.data && empresa.data && copia !== null) {
      const t = setTimeout(() => window.print(), 300);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [r.data, empresa.data, copia]);
  if (r.error) return <p style={{ padding: 24 }}>{mensajeDeError(r.error)}</p>;
  if (!r.data || !empresa.data || copia === null) return <p style={{ padding: 24 }}>Preparando comprobante…</p>;
  const d = r.data;
  return (
    <div className="formato" data-testid="formato-retiro" data-formato="media_carta_vertical" style={{ fontFamily: 'Georgia, serif', fontSize: 10.5, width: '5.1in', margin: '0 auto', padding: 6, position: 'relative' }}>
      <style>{`@page { size: 5.5in 8.5in; margin: 5mm } html, body { background: #fff; margin: 0 } .formato td { padding: 3px 4px; vertical-align: top } .num { text-align: right; font-variant-numeric: tabular-nums } @media screen { body { background: #e5e7eb } .formato { background: #fff } }`}</style>
      <div style={{ borderBottom: '2px solid #000', paddingBottom: 4, marginBottom: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 'bold' }}>{empresa.data.razon_social}</div>
        <div>RIF {empresa.data.rif} · Sucursal {d.sucursal_nombre}</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 4 }}>
          <div style={{ fontWeight: 'bold', fontSize: 11.5 }}>RETIRO PARCIAL DE EFECTIVO</div>
          <div style={{ textAlign: 'right' }}>
            <div>N° <b data-testid="formato-retiro-numero">{d.numero_formateado}</b></div>
            <div>{formatearFechaHora(d.registrado_en)}</div>
          </div>
        </div>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          <tr><td style={{ width: '38%' }}><b>Caja</b></td><td>{d.caja_nombre} · cajero {d.cajero_nombre}</td></tr>
          <tr><td><b>Registrado por</b></td><td>{d.registrado_por_nombre}</td></tr>
          <tr><td><b>Monto retirado</b></td><td style={{ fontSize: 14, fontWeight: 'bold' }} data-testid="formato-retiro-monto">{monto(d)}{d.moneda === 'BS' ? <span style={{ fontSize: 10, fontWeight: 'normal' }}> (≈ $ {Money.parse(d.monto_usd).toDisplay()})</span> : null}</td></tr>
          <tr><td><b>Se entrega a</b></td><td>{d.recibe}</td></tr>
          {d.nota ? <tr><td><b>Nota</b></td><td>{d.nota}</td></tr> : null}
          <tr><td><b>Estado</b></td><td>{d.estado === 'por_confirmar' ? 'Por confirmar en el sistema' : d.estado === 'confirmado' ? `Confirmado por ${d.confirmado_por_nombre ?? ''}${d.confirmado_en ? ` · ${formatearFechaHora(d.confirmado_en)}` : ''}` : `Observado: ${d.comentario ?? ''}`}</td></tr>
        </tbody>
      </table>
      <p style={{ marginTop: 10, fontSize: 9.5 }}>Este efectivo sale de la caja y deja de contarse en su cierre. No es un gasto ni un pago: es dinero de la venta que se entrega para su resguardo.</p>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 46, fontSize: 9.5 }}>
        <span style={{ borderTop: '1px solid #000', paddingTop: 2, width: '45%', textAlign: 'center' }}>Entrega (cajero)</span>
        <span style={{ borderTop: '1px solid #000', paddingTop: 2, width: '45%', textAlign: 'center' }}>Recibe conforme</span>
      </div>
      {copia ? <p style={{ marginTop: 10, fontSize: 9, textAlign: 'center' }} data-testid="formato-retiro-reimpresion">Reimpresión</p> : null}
    </div>
  );
}
